// ============================================================
// documentTheme.ts — the ONE colour theme for the firm's DOCUMENTS (bill PDF —
// server render + print tab + restricted copy — credit notes, the client
// statement, the case summary export). The app UI stays Casevo-branded; only
// documents take the firm's colours (Firm.documentBranding.accentColour, and
// the optional secondaryColour).
//
// Every coloured thing in a document template reads from
// documentTheme(accent, secondary) — no green is hard-coded there. With no
// accent and no secondary the theme is the Casevo greens the documents always
// used (only the title-band pills changed — see "Pills" below).
//
// Readability is built in, never left to the admin's choice:
//   • text ON the accent (title band, balance box, logo mark) is white or
//     near-black — whichever reads better — and the band's gradient is shaded
//     in the direction that keeps that text readable;
//   • accent-coloured TEXT on white (section headings, firm name) is darkened
//     automatically until it meets WCAG AA (4.5:1). The admin is warned when
//     their colour needs this (accentWarning) and shown the darker shade.
//   • Pills (the band's "Interim statute bill · INV-0047" labels) are SOLID:
//     the pill stands out from the band at ≥ 3:1 (against both gradient
//     stops) and its text reads at ≥ 4.5:1 on the pill. The pill starts from
//     the secondary colour (or the accent) and is lightened / darkened only
//     as far as those two rules need (pillOn).
//
// SECONDARY COLOUR (optional). When set it styles the pills, the table header
// rows, the thin divider rules and the balance / total box — each through the
// same automatic text-colour + contrast rules. Unset → those are exactly as
// before (derived from the accent / the neutral greys). The admin is warned
// when it's too close to the accent to tell apart (secondaryWarning).
//
// ★ KEEP IN SYNC with backend/src/lib/documentTheme.ts (identical logic — the
//   browser's print tab and the server's emailed PDF must look the same).
// ============================================================

export const CASEVO_ACCENT = '#2d6a4f'
const NEAR_BLACK = '#1a2420'
const WHITE = '#ffffff'
const BLACK = '#000000'
// WCAG AA for normal-size text.
export const ACCENT_TEXT_MIN_CONTRAST = 4.5
// WCAG non-text contrast — a pill must stand out from the band it sits on.
export const PILL_BAND_MIN_CONTRAST = 3
// CIE76 ΔE below which the secondary reads as "the same colour" as the accent.
export const SECONDARY_MIN_DISTANCE = 20

// '#RGB' / '#RRGGBB' (any case) → '#rrggbb'; anything else → null.
export const normaliseAccent = (v: unknown): string | null => {
  if (typeof v !== 'string') return null
  const s = v.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(s)) return s
  if (/^#[0-9a-f]{3}$/.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
  return null
}

type Rgb = [number, number, number]
const toRgb = (hex: string): Rgb => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb
const toHex = (c: Rgb): string => `#${c.map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('')}`
// a → b by t (0 = a, 1 = b).
const mix = (a: string, b: string, t: number): string => {
  const x = toRgb(a); const y = toRgb(b)
  return toHex([0, 1, 2].map((i) => x[i] + (y[i] - x[i]) * t) as Rgb)
}
const linear = (hex: string): Rgb => toRgb(hex).map((v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4 }) as Rgb
const luminance = (hex: string): number => {
  const lin = linear(hex)
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
}
export const contrastRatio = (a: string, b: string): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((p, q) => q - p)
  return (hi + 0.05) / (lo + 0.05)
}
// Perceptual distance (CIE76 ΔE in CIELAB, D65) — "can you tell these apart?".
const toLab = (hex: string): Rgb => {
  const [r, g, b] = linear(hex)
  const f = (t: number): number => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116)
  const x = f((r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047)
  const y = f(r * 0.2126 + g * 0.7152 + b * 0.0722)
  const z = f((r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883)
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)]
}
export const colourDistance = (a: string, b: string): number => {
  const p = toLab(a); const q = toLab(b)
  return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2])
}
// Step `hex` toward `toward` until it reaches `min` contrast against `bg`.
const pushToContrast = (hex: string, bg: string, toward: string, min: number): string => {
  for (let t = 0; t <= 1.0001; t += 0.02) {
    const c = mix(hex, toward, t)
    if (contrastRatio(c, bg) >= min) return c
  }
  return toward
}
// Accent-coloured text that stays readable on white (darkened only if needed).
export const readableAccentText = (accent: string, bg = WHITE): string =>
  contrastRatio(accent, bg) >= ACCENT_TEXT_MIN_CONTRAST ? accent : pushToContrast(accent, bg, NEAR_BLACK, ACCENT_TEXT_MIN_CONTRAST)

// Text in `hue` on `bg`: the hue pushed toward near-black (light bg) or white
// (dark bg) until it reads at 4.5:1 — so a pill's label is a tinted shade of
// its colour, not flat black/white. null = not reachable even at pure black /
// white (a mid-luminance bg) — the caller moves the bg instead.
const inkOn = (bg: string, hue: string): string | null => {
  const darkInk = contrastRatio(NEAR_BLACK, bg) >= contrastRatio(WHITE, bg)
  const c = pushToContrast(hue, bg, darkInk ? NEAR_BLACK : WHITE, ACCENT_TEXT_MIN_CONTRAST)
  if (contrastRatio(c, bg) >= ACCENT_TEXT_MIN_CONTRAST) return c
  const ext = darkInk ? BLACK : WHITE
  return contrastRatio(ext, bg) >= ACCENT_TEXT_MIN_CONTRAST ? ext : null
}
// A solid pill on a band (gradient `stops`): start from `start`, lighten OR
// darken it (whichever needs the smaller change) just until it stands out
// from every stop at ≥ 3:1 AND its text (a shade of `hue`) reads at ≥ 4.5:1.
// Always solvable: the band's own text colour (white / near-black) already
// clears 4.5:1 against both stops, so the white / black end works.
export const pillOn = (start: string, hue: string, stops: string[]): { bg: string; ink: string } => {
  let best: { t: number; bg: string; ink: string } | null = null
  for (const toward of [WHITE, BLACK]) {
    for (let t = 0; t <= 1.0001; t += 0.02) {
      const bg = mix(start, toward, t)
      if (!stops.every((s) => contrastRatio(bg, s) >= PILL_BAND_MIN_CONTRAST)) continue
      const ink = inkOn(bg, hue)
      if (!ink) continue
      if (!best || t < best.t) best = { t, bg, ink }
      break
    }
  }
  return best ? { bg: best.bg, ink: best.ink } : { bg: WHITE, ink: NEAR_BLACK }
}

// A band in `colour` (title band / balance box / logo mark): white or
// near-black text — whichever has more contrast — and the gradient's other
// stop shaded AWAY from that text (darker under white, lighter under
// near-black) so the whole band stays at least as readable. The colour end
// itself is nudged only if the text on it would fall below 4.5:1 — e.g. a
// bright red under white — so even the band's small labels stay readable.
const bandOf = (colour: string): { from: string; to: string; on: string; onRgb: string } => {
  const whiteText = contrastRatio(WHITE, colour) >= contrastRatio(NEAR_BLACK, colour)
  const textOn = whiteText ? WHITE : NEAR_BLACK
  const end = contrastRatio(textOn, colour) >= ACCENT_TEXT_MIN_CONTRAST ? colour : pushToContrast(colour, textOn, whiteText ? BLACK : WHITE, ACCENT_TEXT_MIN_CONTRAST)
  const other = whiteText ? mix(end, BLACK, 0.42) : mix(end, WHITE, 0.25)
  return {
    from: whiteText ? other : end, to: whiteText ? end : other,
    on: whiteText ? '#fff' : NEAR_BLACK, onRgb: whiteText ? '255,255,255' : toRgb(NEAR_BLACK).join(','),
  }
}

// Admin › Billing: does this accent read as text on white? If not, the darker
// shade the documents will use for headings (offered as a one-click fix).
export const accentWarning = (accent: string): { ok: boolean; ratio: number; suggestion: string | null } => {
  const a = normaliseAccent(accent) ?? CASEVO_ACCENT
  const ratio = contrastRatio(a, WHITE)
  return ratio >= ACCENT_TEXT_MIN_CONTRAST ? { ok: true, ratio, suggestion: null } : { ok: false, ratio, suggestion: readableAccentText(a) }
}
// Admin › Billing: is the secondary too close to the accent to tell apart?
// `adjusted` = the pill had to move off the exact secondary to stay readable.
export const secondaryWarning = (accent: string | null, secondary: string): { similar: boolean; distance: number; adjusted: boolean } => {
  const a = normaliseAccent(accent) ?? CASEVO_ACCENT
  const s = normaliseAccent(secondary) ?? a
  const distance = colourDistance(a, s)
  return { similar: distance < SECONDARY_MIN_DISTANCE, distance, adjusted: documentTheme(a, s).pillBg !== s }
}

export interface DocumentTheme {
  accent: string        // tick bars (decorative)
  bandFrom: string      // title band / logo mark gradient (135deg)
  bandTo: string
  onAccent: string      // text on the band
  onAccentRgb: string   // "r,g,b" of onAccent — the band's translucent facts / subtitle
  firmInk: string       // firm name on the letterhead
  heading: string       // section headings (accent text on white)
  chipBg: string        // case export status chip
  chipInk: string
  // Solid pills on the title band (pillOn — readable + standing out).
  pillBg: string
  pillInk: string
  // ── Secondary-colour surfaces (unset → exactly as before) ──
  hasSecondary: boolean
  rule: string          // the letterhead's divider rule
  secRule: string       // section-heading underlines
  headBg: string        // table header row
  headInk: string
  headRule: string
  balFrom: string       // balance / total box
  balTo: string
  onBal: string
  // The case export's dark (on-screen) variant.
  dark: { heading: string; bandFrom: string; bandTo: string; chipBg: string; chipInk: string; pillBg: string; pillInk: string; rule: string | null; secRule: string | null }
}

const DARK_BG = '#16201b' // the case export's dark page
const PAPER_RULE = '#d8e0dc' // the neutral rule / header-row line
const HEAD_INK = '#7a8880'   // the neutral table-header text

// The Casevo theme — exactly the colours the documents have always printed
// (the pills are the pill rule applied to the Casevo band).
const casevoTheme = (): DocumentTheme => {
  const pill = pillOn(CASEVO_ACCENT, CASEVO_ACCENT, ['#1a3a2a', CASEVO_ACCENT])
  const darkPill = pillOn(CASEVO_ACCENT, CASEVO_ACCENT, ['#1a4230', CASEVO_ACCENT])
  return {
    accent: CASEVO_ACCENT, bandFrom: '#1a3a2a', bandTo: CASEVO_ACCENT, onAccent: '#fff', onAccentRgb: '255,255,255',
    firmInk: '#1a3a2a', heading: CASEVO_ACCENT, chipBg: '#d8f3e8', chipInk: CASEVO_ACCENT,
    pillBg: pill.bg, pillInk: pill.ink,
    hasSecondary: false, rule: CASEVO_ACCENT, secRule: PAPER_RULE, headBg: 'transparent', headInk: HEAD_INK, headRule: PAPER_RULE,
    balFrom: '#1a3a2a', balTo: CASEVO_ACCENT, onBal: '#fff',
    dark: { heading: '#7bc99b', bandFrom: '#1a4230', bandTo: CASEVO_ACCENT, chipBg: 'rgba(123,201,155,0.16)', chipInk: '#8fd6b0', pillBg: darkPill.bg, pillInk: darkPill.ink, rule: null, secRule: null },
  }
}
let CASEVO_THEME: DocumentTheme | null = null

const accentTheme = (accent: string): DocumentTheme => {
  const band = bandOf(accent)
  const heading = readableAccentText(accent)
  const chipBg = mix(accent, WHITE, 0.85)
  const darkHeading = contrastRatio(accent, DARK_BG) >= ACCENT_TEXT_MIN_CONTRAST ? accent : pushToContrast(accent, DARK_BG, WHITE, ACCENT_TEXT_MIN_CONTRAST)
  const [r, g, b] = toRgb(darkHeading)
  const pill = pillOn(accent, accent, [band.from, band.to])
  return {
    accent, bandFrom: band.from, bandTo: band.to, onAccent: band.on, onAccentRgb: band.onRgb,
    firmInk: readableAccentText(band.from),
    heading,
    chipBg,
    chipInk: contrastRatio(heading, chipBg) >= ACCENT_TEXT_MIN_CONTRAST ? heading : pushToContrast(heading, chipBg, NEAR_BLACK, ACCENT_TEXT_MIN_CONTRAST),
    pillBg: pill.bg, pillInk: pill.ink,
    hasSecondary: false, rule: accent, secRule: PAPER_RULE, headBg: 'transparent', headInk: HEAD_INK, headRule: PAPER_RULE,
    balFrom: band.from, balTo: band.to, onBal: band.on,
    dark: { heading: darkHeading, bandFrom: band.from, bandTo: band.to, chipBg: `rgba(${r},${g},${b},0.16)`, chipInk: darkHeading, pillBg: pill.bg, pillInk: pill.ink, rule: null, secRule: null },
  }
}

export const documentTheme = (accentColour?: string | null, secondaryColour?: string | null): DocumentTheme => {
  const accent = normaliseAccent(accentColour)
  const base = !accent || accent === CASEVO_ACCENT ? (CASEVO_THEME ??= casevoTheme()) : accentTheme(accent)
  const secondary = normaliseAccent(secondaryColour)
  if (!secondary) return base
  // The secondary's surfaces — each with the same text / contrast rules.
  const pill = pillOn(secondary, secondary, [base.bandFrom, base.bandTo])
  const darkPill = pillOn(secondary, secondary, [base.dark.bandFrom, base.dark.bandTo])
  const bal = bandOf(secondary)
  const headBg = mix(secondary, WHITE, 0.88)
  return {
    ...base,
    pillBg: pill.bg, pillInk: pill.ink,
    hasSecondary: true,
    rule: secondary,
    secRule: mix(secondary, WHITE, 0.45),
    headBg,
    headInk: contrastRatio(secondary, headBg) >= ACCENT_TEXT_MIN_CONTRAST ? secondary : pushToContrast(secondary, headBg, NEAR_BLACK, ACCENT_TEXT_MIN_CONTRAST),
    headRule: secondary,
    balFrom: bal.from, balTo: bal.to, onBal: bal.on,
    dark: { ...base.dark, pillBg: darkPill.bg, pillInk: darkPill.ink, rule: secondary, secRule: mix(secondary, DARK_BG, 0.5) },
  }
}
